import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * FIBES: per-line subscription upsell.
 *
 * Wraps a single one-time cart line's upsell card. On click it converts that
 * line to its default subscription selling plan (resolved server-side in
 * snippets/cart-products.liquid and passed via data-selling-plan), keeping the
 * same quantity. One-directional: there is no toggle back to one-time.
 *
 * After the change it requests the cart sections and dispatches a
 * CartUpdateEvent so every cart-items-component (drawer + page) morphs itself.
 * Once the line is a subscription, cart-products.liquid no longer renders this
 * card for it, so the upsell disappears on its own.
 */
class CartSubscriptionUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sub-upsell-convert]');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = async () => {
    const lineKey = this.dataset.lineKey;
    const sellingPlan = this.dataset.sellingPlan;
    const quantity = Number(this.dataset.quantity);

    if (!lineKey || !sellingPlan) return;

    this.setAttribute('aria-busy', 'true');
    if (this.button) this.button.disabled = true;

    // Update every cart surface currently in the DOM (drawer + cart page).
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.add(el.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: lineKey,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : undefined,
      selling_plan: sellingPlan,
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const cart = await response.json();

      if (cart.status || cart.errors) {
        console.error('[cart-subscription-upsell] conversion failed', cart);
        this.#reset();
        return;
      }

      // Let each cart-items-component morph its own section from the payload.
      document.dispatchEvent(
        new CartUpdateEvent({}, 'cart-subscription-upsell', {
          source: 'cart-subscription-upsell',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
    } catch (error) {
      console.error('[cart-subscription-upsell]', error);
      this.#reset();
    }
  };

  #reset() {
    this.removeAttribute('aria-busy');
    if (this.button) this.button.disabled = false;
  }
}

if (!customElements.get('cart-subscription-upsell')) {
  customElements.define('cart-subscription-upsell', CartSubscriptionUpsell);
}